/**
 * Authentication Service
 * Manages JWT token storage and retrieval
 */

import { persistentLogger } from '../utils/PersistentLogger';

const TOKEN_KEY = 'book-admin-auth-token';
const USER_NAME_KEY = 'book-admin-user-name';
const ALLOWED_FOLDERS_KEY = 'book-admin-allowed-folders';
const ACCESS_LEVEL_KEY = 'book-admin-access-level';

export type AccessLevel = 'FULL_ACCESS' | 'KRIYADOCS_USER';

export class AuthService {
  /**
   * Store authentication token, user name, allowed folders, and access level
   */
  static setAuth(token: string, name: string, allowedFolders?: string[], accessLevel?: AccessLevel): void {
    persistentLogger.log('AuthService', 'setAuth() called', {
      hasToken: !!token,
      tokenLength: token?.length,
      name,
      allowedFolders,
      accessLevel,
    });
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_NAME_KEY, name);
    if (allowedFolders) {
      localStorage.setItem(ALLOWED_FOLDERS_KEY, JSON.stringify(allowedFolders));
    }
    if (accessLevel) {
      localStorage.setItem(ACCESS_LEVEL_KEY, accessLevel);
    }

    const stored = localStorage.getItem(TOKEN_KEY);
    persistentLogger.log('AuthService', 'setAuth() completed', {
      stored: !!stored,
      storedLength: stored?.length,
      matches: stored === token,
    });
  }

  /**
   * Get stored authentication token
   */
  static getToken(): string | null {
    const token = localStorage.getItem(TOKEN_KEY);
    persistentLogger.log('AuthService', 'getToken() called', { 
      hasToken: !!token,
      tokenLength: token?.length,
      allKeys: Object.keys(localStorage).filter(k => k.includes('auth'))
    });
    return token;
  }

  /**
   * Get stored user name
   */
  static getUserName(): string | null {
    return localStorage.getItem(USER_NAME_KEY);
  }

  /**
   * Get stored allowed folders
   */
  static getAllowedFolders(): string[] | null {
    const stored = localStorage.getItem(ALLOWED_FOLDERS_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return null;
      }
    }
    return null;
  }

  /**
   * Get stored access level (FULL_ACCESS | KRIYADOCS_USER)
   */
  static getAccessLevel(): AccessLevel | null {
    const stored = localStorage.getItem(ACCESS_LEVEL_KEY);
    if (stored === 'FULL_ACCESS' || stored === 'KRIYADOCS_USER') return stored;
    return null;
  }

  /**
   * Check if user has access to a specific folder
   */
  static hasAccessToFolder(folder: string): boolean {
    const allowedFolders = this.getAllowedFolders();
    // Default to full access if no folders stored (legacy tokens)
    if (!allowedFolders) return true;
    return allowedFolders.includes(folder);
  }

  /**
   * Check if user is authenticated
   */
  static isAuthenticated(): boolean {
    const token = this.getToken();
    const isAuth = token !== null;
    persistentLogger.log('AuthService', 'isAuthenticated() called', { isAuth, hasToken: !!token });
    return isAuth;
  }

  /** Event dispatched when auth is cleared so UI (e.g. AuthContext) can update without a full reload */
  static readonly AUTH_CLEARED_EVENT = 'auth-cleared';

  /**
   * Clear authentication data
   */
  static clearAuth(): void {
    persistentLogger.warn('AuthService', 'clearAuth() called - removing token, user name, allowed folders, and access level');
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_NAME_KEY);
    localStorage.removeItem(ALLOWED_FOLDERS_KEY);
    localStorage.removeItem(ACCESS_LEVEL_KEY);

    window.dispatchEvent(new CustomEvent(AuthService.AUTH_CLEARED_EVENT));

    // Verify it was cleared
    const tokenAfter = localStorage.getItem(TOKEN_KEY);
    persistentLogger.log('AuthService', 'clearAuth() completed', { tokenStillExists: !!tokenAfter });
  }

  /**
   * Get Authorization header value
   */
  static getAuthHeader(): string | null {
    const token = this.getToken();
    return token ? `Bearer ${token}` : null;
  }
}

